import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Download, FileText, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: "bg-slate-500/10 text-slate-700 border-slate-500/30",
  issued: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  submitted: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
};

const blank = {
  employee_id: "",
  employee_name: "",
  first_day_worked: "",
  last_day_paid: "",
  final_pay_period_end: "",
  reason_code: "A",
  reason_detail: "",
  expected_return_date: "",
  insurable_earnings_total: 0,
  insurable_hours_total: 0,
  vacation_pay: 0,
  severance_pay: 0,
  notes: "",
  status: "draft",
};

export default function ROEs() {
  const [loading, setLoading] = useState(true);
  const [roes, setRoes] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [roeRes, codesRes, empRes] = await Promise.all([
        axios.get(`${API}/roes/`, { headers }),
        axios.get(`${API}/roes/reason-codes`, { headers }),
        axios.get(`${API}/employees`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRoes(roeRes.data || []);
      setReasonCodes(codesRes.data || []);
      const empData = empRes.data;
      setEmployees(Array.isArray(empData) ? empData : empData.employees || []);
    } catch {
      toast.error("Failed to load ROE data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openNew = () => {
    setEditingId(null);
    setForm(blank);
    setShowDialog(true);
  };

  const openEdit = (roe) => {
    setEditingId(roe.id);
    setForm({ ...blank, ...roe });
    setShowDialog(true);
  };

  const onEmployeeChange = (empId) => {
    const emp = employees.find((e) => e.id === empId);
    setForm({ ...form, employee_id: empId, employee_name: emp?.name || form.employee_name });
  };

  const save = async () => {
    if (!form.employee_id) {
      toast.error("Please select an employee");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/roes/${editingId}`, form, { headers });
        toast.success("ROE updated");
      } else {
        await axios.post(`${API}/roes/`, form, { headers });
        toast.success("ROE created");
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ROE");
    }
  };

  const removeRoe = async (roe) => {
    if (!window.confirm(`Delete ROE for ${roe.employee_name || "this employee"}?`)) return;
    try {
      await axios.delete(`${API}/roes/${roe.id}`, { headers });
      toast.success("ROE deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete ROE");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/roes/export/csv`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roes_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center" data-testid="roes-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="roes-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            Records of Employment
          </h1>
          <p className="text-muted-foreground mt-1">
            Track ROEs issued when employees experience an interruption of earnings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={roes.length === 0} data-testid="export-roes-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={openNew} data-testid="new-roe-btn">
            <Plus className="h-4 w-4 mr-2" />
            New ROE
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-total"><CardHeader className="pb-2"><CardDescription className="text-xs uppercase">Total ROEs</CardDescription><CardTitle className="text-3xl">{roes.length}</CardTitle></CardHeader></Card>
        <Card data-testid="kpi-draft"><CardHeader className="pb-2"><CardDescription className="text-xs uppercase">Draft</CardDescription><CardTitle className="text-3xl">{roes.filter(r => r.status === "draft").length}</CardTitle></CardHeader></Card>
        <Card data-testid="kpi-issued"><CardHeader className="pb-2"><CardDescription className="text-xs uppercase">Issued</CardDescription><CardTitle className="text-3xl">{roes.filter(r => r.status === "issued").length}</CardTitle></CardHeader></Card>
        <Card data-testid="kpi-submitted"><CardHeader className="pb-2"><CardDescription className="text-xs uppercase">Submitted</CardDescription><CardTitle className="text-3xl">{roes.filter(r => r.status === "submitted").length}</CardTitle></CardHeader></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Records</CardTitle>
          <CardDescription>Newest first</CardDescription>
        </CardHeader>
        <CardContent>
          {roes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-40" />
              <p>No ROEs yet.</p>
              <p className="text-sm">Create one when an employee experiences an interruption of earnings.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Last Day Paid</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Earnings</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roes.map((r) => {
                    const code = reasonCodes.find((c) => c.code === r.reason_code);
                    return (
                      <TableRow key={r.id} data-testid={`roe-${r.id}`}>
                        <TableCell className="font-medium">{r.employee_name || r.employee_id}</TableCell>
                        <TableCell>{r.last_day_paid || "-"}</TableCell>
                        <TableCell>
                          <span className="text-xs">
                            <span className="font-mono">{r.reason_code}</span> · {code?.label || r.reason_detail || ""}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">${Number(r.insurable_earnings_total || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{r.insurable_hours_total || 0}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={STATUS_COLORS[r.status] || ""}>{r.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-roe-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => removeRoe(r)} className="text-destructive hover:text-destructive" data-testid={`delete-roe-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" data-testid="roe-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit ROE" : "New Record of Employment"}</DialogTitle>
            <DialogDescription>
              Capture the details of the interruption of earnings. You can save as draft and update later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Employee *</Label>
                <Select value={form.employee_id} onValueChange={onEmployeeChange}>
                  <SelectTrigger data-testid="employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Reason Code *</Label>
                <Select value={form.reason_code} onValueChange={(v) => setForm({ ...form, reason_code: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {reasonCodes.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} — {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>First Day Worked</Label>
                <Input type="date" value={form.first_day_worked || ""} onChange={(e) => setForm({ ...form, first_day_worked: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Last Day Paid</Label>
                <Input type="date" value={form.last_day_paid || ""} onChange={(e) => setForm({ ...form, last_day_paid: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Final Period End</Label>
                <Input type="date" value={form.final_pay_period_end || ""} onChange={(e) => setForm({ ...form, final_pay_period_end: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Expected Return (if applicable)</Label>
                <Input type="date" value={form.expected_return_date || ""} onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="issued">Issued</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Insurable Earnings</Label>
                <Input type="number" step="0.01" value={form.insurable_earnings_total} onChange={(e) => setForm({ ...form, insurable_earnings_total: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label>Insurable Hours</Label>
                <Input type="number" step="0.1" value={form.insurable_hours_total} onChange={(e) => setForm({ ...form, insurable_hours_total: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Vacation Pay</Label>
                <Input type="number" step="0.01" value={form.vacation_pay} onChange={(e) => setForm({ ...form, vacation_pay: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label>Severance Pay</Label>
                <Input type="number" step="0.01" value={form.severance_pay} onChange={(e) => setForm({ ...form, severance_pay: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Reason Detail (especially for code K)</Label>
              <Input value={form.reason_detail || ""} onChange={(e) => setForm({ ...form, reason_detail: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-roe-btn">{editingId ? "Save Changes" : "Create ROE"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
